import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/nextjs";

import { SpotToast } from "@/components/SpotToast";
import { LanguagesDialog } from "@/components/Assistant/LanguagesDialog";
import {
  AgentLanguageInput,
  AssistantsService,
  CoWorkerSettingsInput,
} from "@/generated";
import {
  AgentIntroductoryMessage,
  LanguageOption,
} from "@/types/api/spotflow/spotflow-config";
import { BROWSER_LANGUAGE } from "@/i18n/locales";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";

type Props = {
  /** The deployment's language select: a locale, "browser", or unset. */
  language: string | undefined;
  locales: LanguageOption[];
  agentLanguages: AgentIntroductoryMessage[];
  agentDefaultLanguage: string | null;
  agentEditable: boolean;
  assistantId: string | null;
  isDisabled?: boolean;
};

type Greetings = {
  languages: AgentLanguageInput[];
  defaultLanguage: string | null;
};

type Draft = Greetings & { original: Greetings };

type GreetingEntry = { code: string; text?: string | null };

type Gaps = { empty: LanguageOption[]; absent: LanguageOption[] };

const primarySubtag = (tag: string) => tag.split("-")[0].toLowerCase();

const listLabels = (labels: string[]) =>
  labels.length <= 1
    ? (labels[0] ?? "")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

const sameGreetings = (a: Greetings, b: Greetings) =>
  a.defaultLanguage === b.defaultLanguage &&
  JSON.stringify(a.languages) === JSON.stringify(b.languages);

/** Under a deployment's Language select: the agent has no welcome message
 *  for what that select will greet in, and here is where to add it. The
 *  messages live on the agent and are shared by every deployment. */
export const IntroLanguagesWarning = ({
  language,
  locales,
  agentLanguages,
  agentDefaultLanguage,
  agentEditable,
  assistantId,
  isDisabled,
}: Props) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  // The dialog labels the widget's locales plus whatever else the agent
  // already greets in.
  const options = useMemo((): LanguageOption[] => {
    const known = new Set(locales.map((locale) => locale.value));
    return [
      ...locales,
      ...agentLanguages
        .filter((entry) => !known.has(entry.code))
        .map((entry) => ({
          value: entry.code,
          label: entry.label,
          tag: entry.code,
        })),
    ];
  }, [locales, agentLanguages]);
  const labelByCode = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options]
  );
  const isBrowser = language === BROWSER_LANGUAGE;

  // Nothing to warn about while the agent greets nobody: that is the
  // agent's own gap, not a language one. A language listed with no text is
  // still picked, so its visitors get no welcome message at all; one not
  // listed falls through to the browser language, then the default.
  const missingFor = (entries: GreetingEntry[]): Gaps => {
    const greeted = new Set(
      entries.filter((entry) => entry.text?.trim()).map((entry) => entry.code)
    );
    if (!language || greeted.size === 0) return { empty: [], absent: [] };
    const listed = new Set(entries.map((entry) => entry.code));
    const code = primarySubtag(language);
    const targets = isBrowser
      ? locales
      : [
          locales.find((option) => option.value === code) ?? {
            value: code,
            label: labelByCode.get(code) ?? code,
          },
        ];
    const gaps = targets.filter((target) => !greeted.has(target.value));
    return {
      empty: gaps.filter((gap) => listed.has(gap.value)),
      absent: gaps.filter((gap) => !listed.has(gap.value)),
    };
  };
  const hasGaps = (gaps: Gaps) => gaps.empty.length + gaps.absent.length > 0;

  const agentEntries = agentLanguages.map((entry) => ({
    code: entry.code,
    text: entry.text,
  }));
  const missing = missingFor(agentEntries);
  // Inside the dialog the check follows the draft, so the warning goes away
  // as soon as the language gets a message.
  const draftEntries = draft?.languages.map((entry) => ({
    code: entry.code,
    text: entry.introductory_message?.[0]?.text,
  }));
  const draftMissing = draftEntries ? missingFor(draftEntries) : null;

  // A background refetch may report the gap filled while the dialog is
  // open: it stays, closing it is what saves the draft.
  if (!hasGaps(missing) && !draft) return null;

  // The backend's last resort: the default language when the agent lists
  // it, otherwise its first language.
  const fallbackLabel = (
    entries: GreetingEntry[],
    defaultLanguage: string | null
  ) => {
    const code = entries.some((entry) => entry.code === defaultLanguage)
      ? defaultLanguage
      : entries[0]?.code;
    return code ? (labelByCode.get(code) ?? code) : null;
  };
  const copy = (
    gaps: Gaps,
    entries: GreetingEntry[],
    defaultLanguage: string | null
  ) => {
    const labels = (options: LanguageOption[]) =>
      listLabels(options.map((option) => option.label));
    const fallback = fallbackLabel(entries, defaultLanguage);
    const sentences: string[] = [];
    if (gaps.empty.length > 0) {
      sentences.push(
        isBrowser
          ? `Visitors in ${labels(gaps.empty)} get no welcome message, as it is empty.`
          : `Visitors get no welcome message, as the ${labels(gaps.empty)} one is empty.`
      );
    }
    if (gaps.absent.length > 0 && fallback) {
      sentences.push(
        isBrowser
          ? `Visitors in ${labels(gaps.absent)} get the ${fallback} one instead.`
          : `Visitors get the one in their browser's language instead, or the ${fallback} one if there is none.`
      );
    }
    sentences.push(
      agentEditable
        ? "Welcome messages are shared by all deployments of this agent."
        : "This deployment runs a pinned version, so new welcome messages apply once it moves to the current one."
    );
    return {
      title: isBrowser
        ? "Missing welcome messages for some browser languages"
        : `Missing welcome message in ${labels([...gaps.empty, ...gaps.absent])}`,
      description: sentences.join(" "),
    };
  };
  const dialogCopy =
    draft && draftEntries && draftMissing && hasGaps(draftMissing)
      ? copy(draftMissing, draftEntries, draft.defaultLanguage)
      : null;

  const loadGreetings = async (): Promise<{
    definition: CoWorkerSettingsInput;
    greetings: Greetings;
  }> => {
    const { data } = await AssistantsService.getAssistant({
      path: { group_id: spotGroup.id, assistant_id: assistantId ?? "" },
    });
    const definition = (data?.definition ?? {}) as CoWorkerSettingsInput;
    return {
      definition,
      greetings: {
        languages: definition.languages ?? [],
        defaultLanguage: definition.default_language ?? null,
      },
    };
  };

  const openDialog = async () => {
    if (!assistantId || isOpening || draft) return;
    setIsOpening(true);
    try {
      const { greetings } = await loadGreetings();
      setDraft({ ...greetings, original: greetings });
      setIsOpen(true);
    } catch (error) {
      SpotToast.error("Couldn't load the agent's welcome messages.");
      Sentry.captureException(error);
    } finally {
      setIsOpening(false);
    }
  };

  // Saved onto what the agent holds now, not the copy taken when the dialog
  // opened: the agent may have been edited elsewhere in the meantime, and
  // the update replaces the whole definition.
  const saveDraft = async (current: Greetings) => {
    const { definition } = await loadGreetings();
    await AssistantsService.updateAssistant({
      path: { group_id: spotGroup.id, assistant_id: assistantId ?? "" },
      body: {
        definition: {
          ...definition,
          languages: current.languages,
          default_language: current.defaultLanguage,
        },
      },
    });
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.DEPLOYMENT_CONFIG],
      }),
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] }),
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANT_ID] }),
    ]);
  };

  const closeDialog = async () => {
    if (!draft || !assistantId) return;
    setIsOpen(false);
    if (sameGreetings(draft, draft.original)) {
      setDraft(null);
      return;
    }
    try {
      await saveDraft(draft);
      setDraft(null);
    } catch (error) {
      SpotToast.error(
        "Couldn't save the agent's welcome messages. Your changes are still here."
      );
      Sentry.captureException(error);
      setIsOpen(true);
    }
  };

  return (
    <>
      {hasGaps(missing) && (
        <NoxusAlert
          look="banner"
          variant="warning"
          size="xs"
          direction="vertical"
          {...copy(missing, agentEntries, agentDefaultLanguage)}
          action={
            agentEditable && assistantId && !isDisabled
              ? {
                  label: isOpening ? "Opening" : "Manage welcome messages",
                  onClick: openDialog,
                }
              : undefined
          }
        />
      )}
      {draft && (
        <LanguagesDialog
          isOpen={isOpen}
          onOpenChange={(open) => {
            if (!open) void closeDialog();
          }}
          languages={draft.languages}
          onLanguagesChange={(languages) =>
            setDraft((current) => (current ? { ...current, languages } : null))
          }
          defaultLanguage={draft.defaultLanguage}
          onDefaultLanguageChange={(defaultLanguage) =>
            setDraft((current) =>
              current ? { ...current, defaultLanguage } : null
            )
          }
          options={options}
          addableCodes={locales.map((locale) => locale.value)}
          notice={
            dialogCopy && (
              <NoxusAlert
                look="banner"
                variant="warning"
                size="xs"
                title={dialogCopy.title}
                description={dialogCopy.description}
              />
            )
          }
        />
      )}
    </>
  );
};
